// Dispute detail — Design Pack §7.2.
//
// Status banner with state + SLA hint, conversation pane (parties-visible
// messages), evidence panel, link to the public Dispute Resolution policy.
// Parties post via audience='parties'; admins see team_only too via the
// admin tooling page (Wave 3b owns that surface). Here we render whichever
// messages the API returned for the current user.

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { StateBadge } from '../../components/StateBadge';
import { MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders, useAuth } from '../../lib/auth-context';

export const Route = createFileRoute('/disputes/$id')({
  component: DisputeDetail,
});

type DisputeState = 'opened' | 'acknowledged' | 'under_review' | 'resolved' | 'closed';

type DisputeDto = {
  id: string;
  exchange_id: string;
  opened_by_user_id: string;
  state: DisputeState;
  category: 'safety' | 'misrepresentation' | 'cancellation' | 'harassment' | 'cash_dispute' | 'other';
  description: string;
  evidence_urls: string[] | null;
  resolution_outcome:
    | 'warning'
    | 'account_suspended'
    | 'account_closed'
    | 'venue_paused'
    | 'mediated_no_action'
    | 'not_actionable'
    | null;
  resolution_summary: string | null;
  opened_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  short_id: string;
};

type MessageDto = {
  id: string;
  dispute_id: string;
  author_user_id: string;
  audience: 'parties' | 'team_only';
  body: string;
  created_at: string;
};

type ExchangeStub = {
  id: string;
  state: string;
  thread_id: string | null;
  artist_user_id: string;
  host_user_id: string;
  venue_name: string;
};

/**
 * Evidence shape — visible variant. Returned by /api/disputes/:id for the
 * uploader and admin (Model A — private-by-default).
 */
type EvidenceVisible = {
  id: string;
  uploader_user_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  download_url: string;
  is_private: false;
};

/** Redacted placeholder seen by the OTHER party. No filename, no download. */
type EvidenceRedacted = {
  id: string;
  uploader_user_id: string;
  is_private: true;
};

type EvidenceDto = EvidenceVisible | EvidenceRedacted;

const ALLOWED_EVIDENCE_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,application/zip';
const MAX_EVIDENCE_BYTES = 10 * 1024 * 1024;

function DisputeDetail() {
  const { id: disputeId } = Route.useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dispute, setDispute] = useState<DisputeDto | null>(null);
  const [exchange, setExchange] = useState<ExchangeStub | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [evidence, setEvidence] = useState<EvidenceDto[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [draftMsg, setDraftMsg] = useState('');
  const [posting, setPosting] = useState(false);
  // FUNC-216: parties can pick "Both parties" (default) or "WWAAVE team only".
  // Server route accepts both; visibility on GET filters team_only away from
  // the OTHER party while still surfacing it to the author + admins.
  const [audience, setAudience] = useState<'parties' | 'team_only'>('parties');

  async function reload() {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`/api/disputes/${disputeId}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 404) setErrorMsg('We could not find that dispute.');
        else if (res.status === 403) setErrorMsg('You are not on this dispute.');
        else setErrorMsg('Something went wrong loading this dispute.');
        return;
      }
      const data = (await res.json()) as {
        dispute: DisputeDto;
        exchange: ExchangeStub;
        messages: MessageDto[];
        evidence?: EvidenceDto[];
      };
      setDispute(data.dispute);
      setExchange(data.exchange);
      setMessages(data.messages);
      setEvidence(data.evidence ?? []);
    } catch {
      setErrorMsg('Something went wrong loading this dispute.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disputeId]);

  async function postMessage() {
    if (draftMsg.trim().length === 0) return;
    setPosting(true);
    try {
      const res = await fetch(`/api/disputes/${disputeId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          dispute_id: disputeId,
          audience,
          body: draftMsg,
        }),
      });
      if (!res.ok) {
        const j = await safeJson(res);
        notifications.show({
          message: j?.error?.message ?? 'Could not send.',
          color: 'red',
        });
        return;
      }
      setDraftMsg('');
      await reload();
    } catch {
      notifications.show({ message: 'Could not send.', color: 'red' });
    } finally {
      setPosting(false);
    }
  }

  if (loading) {
    return (
      <AppShell tone="content">
        <Loader />
      </AppShell>
    );
  }

  if (errorMsg !== null || dispute === null || exchange === null) {
    return (
      <AppShell tone="content">
        <Alert color="red" title="We hit a snag">
          {errorMsg ?? 'Dispute not found.'}
        </Alert>
        <Box mt="md">
          <Anchor component={Link} to="/dashboard">
            Back to dashboard
          </Anchor>
        </Box>
      </AppShell>
    );
  }

  const isResolved = dispute.state === 'resolved' || dispute.state === 'closed';
  const isClosed = dispute.state === 'closed';
  // Party detection — drives respondent affordances. The /api/disputes/:id
  // response already gates visibility, so any signed-in user reaching this
  // branch is either a party or an admin. We surface respondent UI only
  // when the user matches one of the two exchange parties.
  const isHost = user !== null && user.id === exchange.host_user_id;
  const isArtist = user !== null && user.id === exchange.artist_user_id;
  const isParty = isHost || isArtist;
  // Respondent — the party who did NOT open the dispute. This is the
  // user who needs the acknowledge affordance.
  const isRespondent = isParty && user !== null && user.id !== dispute.opened_by_user_id;

  return (
    <AppShell tone="content">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Trust & safety"
            title={`Dispute #${dispute.short_id}`}
            lede={
              <>
                On exchange at {exchange.venue_name}.{' '}
                <Anchor href={`/exchanges/${exchange.id}`} c="brand.7">
                  View the exchange
                </Anchor>
              </>
            }
            action={<StateBadge entity="dispute" state={dispute.state} size="lg" />}
          />
        </MotionSection>

        <StatusBanner dispute={dispute} />

        {isRespondent && dispute.state === 'opened' ? (
          <RespondentAcknowledgeCard disputeId={disputeId} onAcknowledged={reload} />
        ) : null}

        <Card withBorder>
          <Stack gap="xs">
            <Title order={4}>What was raised</Title>
            <Text size="sm" c="dimmed">
              Category: {humanCategory(dispute.category)} · Opened{' '}
              {formatDate(dispute.opened_at)}
            </Text>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{dispute.description}</Text>
            {dispute.evidence_urls !== null && dispute.evidence_urls.length > 0 ? (
              <Box>
                <Text size="sm" fw={500} mt="xs">
                  Evidence
                </Text>
                <Stack gap={4} mt={4}>
                  {dispute.evidence_urls.map((url) => (
                    <Anchor key={url} href={url} target="_blank" rel="noopener noreferrer" size="sm">
                      {url}
                    </Anchor>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Card>

        {isResolved && dispute.resolution_summary !== null ? (
          <Card withBorder>
            <Stack gap="xs">
              <Title order={4}>Outcome</Title>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{dispute.resolution_summary}</Text>
              {dispute.resolved_at !== null ? (
                <Text size="xs" c="dimmed">
                  Resolved {formatDate(dispute.resolved_at)}
                </Text>
              ) : null}
            </Stack>
          </Card>
        ) : null}

        <Box>
          <Title order={4} mb="xs">
            Conversation
          </Title>
          {messages.length === 0 ? (
            <Text c="dimmed">No messages yet. Our team will be in touch.</Text>
          ) : (
            <Stack gap="sm">
              {messages.map((m) => (
                <Card key={m.id} withBorder padding="sm">
                  <Group justify="space-between" mb={4}>
                    <Text size="xs" c="dimmed">
                      {formatDate(m.created_at)}
                    </Text>
                    {m.audience === 'team_only' ? (
                      <Badge size="xs" color="gray" variant="light">
                        Team only
                      </Badge>
                    ) : null}
                  </Group>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{m.body}</Text>
                </Card>
              ))}
            </Stack>
          )}
        </Box>

        {!isClosed ? (
          <>
            <Divider />
            <Stack gap="sm">
              <Title order={5}>Add to the conversation</Title>
              {isParty ? (
                <Radio.Group
                  value={audience}
                  onChange={(v) => setAudience(v === 'team_only' ? 'team_only' : 'parties')}
                  label="Who can read this"
                  description="Team-only notes go to WWAAVE staff only. The other party won't see them."
                >
                  <Stack gap={4} mt={4}>
                    <Radio value="parties" label="Share with both parties" />
                    <Radio
                      value="team_only"
                      label="Share with the WWAAVE team only"
                    />
                  </Stack>
                </Radio.Group>
              ) : null}
              <Textarea
                minRows={3}
                maxLength={1500}
                value={draftMsg}
                onChange={(e) => setDraftMsg(e.currentTarget.value)}
                placeholder="What else does our team need to know?"
                disabled={isResolved}
              />
              <Group justify="flex-end">
                <Button
                  onClick={postMessage}
                  loading={posting}
                  disabled={draftMsg.trim().length === 0 || isResolved}
                >
                  Send
                </Button>
              </Group>
            </Stack>
          </>
        ) : null}

        <EvidenceSection
          disputeId={disputeId}
          evidence={evidence}
          canUpload={isParty && !isClosed}
          viewerUserId={user?.id ?? null}
          artistUserId={exchange.artist_user_id}
          hostUserId={exchange.host_user_id}
          onUploaded={reload}
        />

        <Divider />
        <Text size="sm" c="dimmed">
          Read the full{' '}
          <Anchor component={Link} to="/dispute-resolution">
            Dispute Resolution policy
          </Anchor>
          .
        </Text>
      </Stack>
    </AppShell>
  );
}

/**
 * Respondent acknowledgement card — FUNC-216.
 *
 * The party who did NOT open the dispute records they have read it. POSTs to
 * `/api/disputes/:id/respondent-acknowledge`, which drives
 * `respondent.acknowledge` in the dispute state machine and lands the dispute
 * in `acknowledged` (same target state as admin.acknowledge — keeps the SLA
 * + downstream "team is reading this" copy on one track).
 */
function RespondentAcknowledgeCard({
  disputeId,
  onAcknowledged,
}: {
  disputeId: string;
  onAcknowledged: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function ack() {
    setBusy(true);
    setErr(null);
    try {
      await fetchOk(`/api/disputes/${disputeId}/respondent-acknowledge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ dispute_id: disputeId }),
      });
      await onAcknowledged();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : 'We could not record your acknowledgement just now.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder>
      <Stack gap="xs">
        <Title order={5}>A concern has been raised about this exchange</Title>
        <Text size="sm">
          Take a moment to read what was shared. When you're ready, let the other
          party know you've seen it.
        </Text>
        <Group>
          <Button variant="light" loading={busy} onClick={ack}>
            I've seen this
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          This won't decide anything. It just confirms you've seen the dispute.
        </Text>
        {err !== null ? (
          <Text size="xs" c="red">
            {err}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}

/**
 * EvidenceSection — Wave 5 N (FUNC-216).
 *
 * Renders the existing evidence list AND, for parties on a non-closed dispute,
 * the single-file uploader. Parties + admins see contents on rows they
 * uploaded (admins see everything); the OTHER party sees redacted placeholders
 * — Model A (private-by-default), enforced server-side. See
 * `routes/disputes.ts` POST /:id/evidence comment for rationale.
 *
 * Upload constraints (mirrored from the server validator so the SPA can
 * surface inline errors before the round trip):
 *   - One file per submit
 *   - Max 10 MB
 *   - image/jpeg, image/png, image/webp, image/heic, image/heif,
 *     application/pdf, application/zip
 */
function EvidenceSection({
  disputeId,
  evidence,
  canUpload,
  viewerUserId,
  artistUserId,
  hostUserId,
  onUploaded,
}: {
  disputeId: string;
  evidence: EvidenceDto[];
  canUpload: boolean;
  viewerUserId: string | null;
  artistUserId: string;
  hostUserId: string;
  onUploaded: () => Promise<void> | void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  function pickFile(next: File | null) {
    setErrMsg(null);
    if (next === null) {
      setFile(null);
      return;
    }
    if (next.size > MAX_EVIDENCE_BYTES) {
      setErrMsg(`${next.name} is over the 10 MB limit.`);
      setFile(null);
      resetRef.current?.();
      return;
    }
    const mime = (next.type || '').toLowerCase();
    if (!ALLOWED_EVIDENCE_ACCEPT.split(',').includes(mime)) {
      setErrMsg('That file type is not supported. Use images, PDF, or zip.');
      setFile(null);
      resetRef.current?.();
      return;
    }
    setFile(next);
  }

  async function submit() {
    if (file === null) return;
    setUploading(true);
    setErrMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      await fetchOk(`/api/disputes/${disputeId}/evidence`, {
        method: 'POST',
        credentials: 'include',
        // NOTE: do not set Content-Type — the browser fills the boundary param.
        headers: { ...csrfHeaders() },
        body: form,
      });
      setFile(null);
      resetRef.current?.();
      await onUploaded();
      notifications.show({ message: 'Evidence added.', color: 'teal' });
    } catch (e) {
      setErrMsg(
        e instanceof ApiError
          ? e.message
          : 'We could not save that file just now.',
      );
    } finally {
      setUploading(false);
    }
  }

  if (!canUpload && evidence.length === 0) return null;

  return (
    <Stack gap="sm">
      <Title order={5}>Evidence</Title>
      {canUpload ? (
        <Card withBorder padding="sm">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Add evidence
            </Text>
            <Text size="xs" c="dimmed">
              One file at a time, up to 10 MB. Images, PDF, or zip. Private,
              visible only to admin and the uploader.
            </Text>
            <Group gap="xs" wrap="wrap">
              <FileButton
                resetRef={resetRef}
                accept={ALLOWED_EVIDENCE_ACCEPT}
                onChange={pickFile}
              >
                {(props) => (
                  <Button {...props} variant={file ? 'light' : 'default'} size="xs">
                    {file ? `Selected: ${file.name}` : 'Choose a file'}
                  </Button>
                )}
              </FileButton>
              <Button
                size="xs"
                onClick={submit}
                disabled={file === null}
                loading={uploading}
              >
                Add
              </Button>
              {file !== null ? (
                <Text size="xs" c="dimmed">
                  {formatEvidenceSize(file.size)}
                </Text>
              ) : null}
            </Group>
            {errMsg !== null ? (
              <Text size="xs" c="red">
                {errMsg}
              </Text>
            ) : null}
          </Stack>
        </Card>
      ) : null}

      {evidence.length === 0 ? (
        <Text size="sm" c="dimmed">
          No evidence has been added yet.
        </Text>
      ) : (
        <Stack gap="xs">
          {evidence.map((ev) => (
            <EvidenceRow
              key={ev.id}
              evidence={ev}
              viewerUserId={viewerUserId}
              artistUserId={artistUserId}
              hostUserId={hostUserId}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function EvidenceRow({
  evidence,
  viewerUserId,
  artistUserId,
  hostUserId,
}: {
  evidence: EvidenceDto;
  viewerUserId: string | null;
  artistUserId: string;
  hostUserId: string;
}) {
  const uploaderLabel = labelForUploader(
    evidence.uploader_user_id,
    viewerUserId,
    artistUserId,
    hostUserId,
  );
  if (evidence.is_private) {
    return (
      <Card withBorder padding="sm">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2}>
            <Group gap={6}>
              <Text size="sm" fw={500}>
                {LockIcon}
                Evidence on file
              </Text>
              <Badge size="xs" color="gray" variant="light">
                Private
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              From {uploaderLabel}. Visible only to admin and the uploader.
            </Text>
          </Stack>
        </Group>
      </Card>
    );
  }
  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={2}>
          <Anchor
            href={evidence.download_url}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            fw={500}
          >
            {evidence.filename}
          </Anchor>
          <Text size="xs" c="dimmed">
            {humanContentType(evidence.content_type)} ·{' '}
            {formatEvidenceSize(evidence.size_bytes)} · From {uploaderLabel} ·{' '}
            {formatDate(evidence.created_at)}
          </Text>
        </Stack>
        <Button
          component="a"
          href={evidence.download_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="light"
          size="xs"
        >
          {evidence.content_type === 'application/zip' ? 'Download' : 'Open'}
        </Button>
      </Group>
    </Card>
  );
}

const LockIcon = (
  <Box component="span" mr={4} aria-hidden style={{ display: 'inline-block' }}>
    {/* tiny inline SVG so we don't pull a new icon dep just for this hint */}
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1a3 3 0 0 0-3 3v2H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3Zm-2 5V4a2 2 0 1 1 4 0v2H6Z" />
    </svg>
  </Box>
);

function labelForUploader(
  uploaderUserId: string,
  viewerUserId: string | null,
  artistUserId: string,
  hostUserId: string,
): string {
  if (viewerUserId !== null && uploaderUserId === viewerUserId) return 'you';
  if (uploaderUserId === artistUserId) return 'the artist';
  if (uploaderUserId === hostUserId) return 'the host';
  return 'the other party';
}

function humanContentType(mime: string): string {
  if (mime === 'application/pdf') return 'PDF';
  if (mime === 'application/zip') return 'Zip';
  if (mime.startsWith('image/')) {
    const sub = mime.slice('image/'.length).toUpperCase();
    return sub === 'JPEG' ? 'JPG' : sub;
  }
  return mime;
}

function formatEvidenceSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBanner({ dispute }: { dispute: DisputeDto }) {
  if (dispute.state === 'opened') {
    return (
      <Alert color="sun" title="Awaiting acknowledgement">
        Our team aims to acknowledge new disputes within 48 hours.
      </Alert>
    );
  }
  if (dispute.state === 'acknowledged') {
    return (
      <Alert color="blue" title="Acknowledged">
        We've received your dispute. We'll begin investigating shortly.
      </Alert>
    );
  }
  if (dispute.state === 'under_review') {
    return (
      <Alert color="blue" title="Under review">
        Our team is investigating. We'll be in touch as it progresses.
      </Alert>
    );
  }
  return null;
}

function humanCategory(c: DisputeDto['category']): string {
  switch (c) {
    case 'safety':
      return 'Safety';
    case 'misrepresentation':
      return 'Misrepresentation';
    case 'cancellation':
      return 'Cancellation';
    case 'harassment':
      return 'Harassment';
    case 'cash_dispute':
      return 'Cash dispute';
    case 'other':
      return 'Other';
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

async function safeJson(res: Response): Promise<{ error?: { message?: string } } | null> {
  try {
    return (await res.json()) as { error?: { message?: string } };
  } catch {
    return null;
  }
}
